/**
 * The donation date is a calendar day, but OPDON persists it as a full UTC instant
 * (the create flow anchors the picked day at 12:00:00Z) and returns it that way
 * (e.g. "2026-10-06T12:00:00Z"). The FE treats `donationDate` as ISO `yyyy-MM-dd`
 * throughout — that's what OPAL's DatePickerInput ↔ display bridge expects — so the
 * read mapper strips the time part. Feeding the raw instant straight to the picker
 * (no `format` set) makes flowbite-datepicker misparse it as garbage like
 * "10/06/0178" (OP-5819).
 */
function toCalendarDate(value) {
    if (!value)
        return "";
    return value.split("T")[0];
}
/**
 * Re-anchors a calendar day (`yyyy-MM-dd`) to the UTC instant OPDON expects on the
 * update wire. Mirrors the create flow's `${date}T12:00:00Z`: 12:00 UTC keeps the
 * calendar day stable across any reasonable viewer timezone, and the explicit `Z`
 * gives Npgsql the UTC Kind its `timestamp with time zone` column requires. Empty
 * input → `undefined` so the field is dropped (backend leaves it unchanged) rather
 * than sent as an unparseable "".
 */
function toDonationDateInstant(value) {
    const date = toCalendarDate(value);
    return date ? `${date}T12:00:00Z` : undefined;
}
/**
 * Maps a BFF donation response to the domain Donation entity.
 */
export function mapApiDonationToDonation(apiDonation) {
    return {
        id: String(apiDonation.donationId),
        label: apiDonation.label ?? "",
        amount: apiDonation.totalAmount ?? 0,
        currency: apiDonation.currency,
        status: apiDonation.transactionStatus,
        paymentRefusalReason: apiDonation.paymentRefusalReason ?? null,
        donationDate: toCalendarDate(apiDonation.donationDate),
        donationType: apiDonation.donationType,
        donorId: apiDonation.donorId ? String(apiDonation.donorId) : "",
        // CRM references behind the donor — these (not donorId) are what the donor link
        // navigates to. Present on the detail read (GetById Includes the donor); absent on
        // list reads, where the donor isn't linked anyway.
        contactId: apiDonation.contactId ? String(apiDonation.contactId) : undefined,
        // Referent contact name (Organization donation) — drives the Summary's "Donor contact" row.
        contactDisplayName: apiDonation.contactDisplayName ?? undefined,
        organizationId: apiDonation.organizationId ? String(apiDonation.organizationId) : undefined,
        donorType: apiDonation.donorType ?? undefined,
        donorName: apiDonation.donorDisplayNameSnapshot ?? "",
        campaignId: apiDonation.campaignId ? String(apiDonation.campaignId) : undefined,
        campaignName: apiDonation.campaignNameSnapshot ?? undefined,
        // Campaign type code resolved live by OPDON from its campaign snapshot. Undefined when the
        // donation has no campaign or the type has not synced from OPINK yet.
        campaignType: apiDonation.campaignTypeCode ?? undefined,
        paymentMethod: apiDonation.paymentMethod,
        natureDon: apiDonation.donationNature,
        donationForm: apiDonation.donationForm ?? undefined,
        donationSource: apiDonation.donationSource ?? undefined,
        chequeNumber: apiDonation.chequeNumber ?? undefined,
        externalTransactionProvider: apiDonation.externalTransactionProvider ?? undefined,
        subscriptionId: apiDonation.subscriptionId ? String(apiDonation.subscriptionId) : undefined,
        notes: apiDonation.note ?? undefined,
        // Comparison-with-previous-donation snapshot. Undefined (not 0) when the donor has no prior
        // validated donation — the chart shows the first-donation message in that case.
        previousDonationAmount: apiDonation.previousDonationAmount ?? undefined,
        donationTrendPercentage: apiDonation.donationTrendPercentage ?? undefined,
        // Donor aggregate stats (present on the detail read; absent on list/search).
        donorStats: apiDonation.donorStats
            ? {
                currency: apiDonation.donorStats.currency ?? undefined,
                donationCount: apiDonation.donorStats.donationCount ?? 0,
                totalDonatedAmount: apiDonation.donorStats.totalDonatedAmount ?? 0,
                averageDonation: apiDonation.donorStats.averageDonation ?? 0,
                firstDonationDate: apiDonation.donorStats.firstDonationDate ?? undefined,
                lastDonationDate: apiDonation.donorStats.lastDonationDate ?? undefined,
            }
            : undefined,
        createdAt: apiDonation.createdAt ?? "",
        updatedAt: apiDonation.updatedAt ?? "",
    };
}
/**
 * Maps a user-clearable text/enum field to its update-wire value.
 *
 * `UpdateDonationRequestDto` distinguishes an omitted property ("leave unchanged")
 * from an explicit `null` ("clear the value"). A field the user emptied in the form
 * arrives as `""`, so it must be sent as `null` to actually clear it on the backend;
 * a field that was never edited stays `undefined` and is dropped so the persisted
 * value is left untouched. (The enum fields — donationForm / donationSource — also
 * can't be deserialized from `""`, so `null` is the only valid clear signal.)
 */
function clearable(value) {
    if (value === undefined)
        return undefined;
    return value === "" ? null : value;
}
/**
 * Maps the domain Donation (edited in the detail form) to the update wire payload,
 * translating the domain field names to the BFF/OPDON ones
 * (amount → totalAmount, status → transactionStatus, natureDon → donationNature,
 * notes → note).
 *
 * The calendar-day `donationDate` is re-anchored to a `T12:00:00Z` UTC instant via
 * {@link toDonationDateInstant} (mirrors the create flow); an empty date is dropped so
 * the backend leaves it unchanged instead of failing to parse "" as a DateTime. Empty
 * id fields are likewise dropped. User-clearable text/enum fields go through
 * {@link clearable} so emptying them sends `null` (clear) rather than being silently
 * dropped. `campaignName` is a server-resolved snapshot the update handler never
 * persists, so it is only forwarded when present (omit, never clear).
 *
 * Donor re-point: the detail form lets the user re-qualify the donor by picking a new
 * Contact (Individual) or Organisation (Organization referent). Those resolve to
 * `contactId` / `organizationId`, dropped when empty so the backend leaves the donor
 * unchanged — and never sent as `null`, because clearing a set donor reference is blocked
 * by the detail form's save gate, not expressed on the wire. `donorDisplayName` (and the
 * referent `contactDisplayName`) carry the picker label so OPDON can recompute the donation
 * label and snapshot the donor name while its OPUS snapshot is still catching up (Kafka lag);
 * the backend uses them only when the donor is actually re-pointed.
 *
 * Intentionally NOT mapped (UpdateDonationRequestDto has no such field — the
 * backend can't persist them, so the detail form renders them read-only):
 *   - `donationType` — immutable after creation
 *   - `currency` — fixed when the donation is created
 *   - `donorId` — the donor is re-pointed via `contactId` / `organizationId`, never by raw id
 */
export function mapDonationToUpdatePayload(donation) {
    return {
        totalAmount: donation.amount,
        transactionStatus: donation.status,
        donationNature: donation.natureDon || undefined,
        donationForm: clearable(donation.donationForm),
        donationSource: clearable(donation.donationSource),
        paymentMethod: donation.paymentMethod || undefined,
        chequeNumber: clearable(donation.chequeNumber),
        note: clearable(donation.notes),
        donationDate: toDonationDateInstant(donation.donationDate),
        campaignId: donation.campaignId || undefined,
        campaignName: donation.campaignName || undefined,
        // Subscription re-link (OP-5175): emptied (`""`) sends `null` to detach; never edited
        // (`undefined`) is dropped so the backend leaves the link untouched. Same clearable
        // semantics as the other id/enum fields.
        subscriptionId: clearable(donation.subscriptionId),
        // Donor re-point: dropped when empty (donor left unchanged). `donorDisplayName` carries the
        // picked donor label — Individual donors use `donorName`; an Organization donor's name is its
        // org name, also held in `donorName`. `contactDisplayName` is the referent name (Org donation).
        contactId: donation.contactId || undefined,
        organizationId: donation.organizationId || undefined,
        donorDisplayName: donation.donorName || undefined,
        contactDisplayName: donation.contactDisplayName || undefined,
    };
}
